import type { Brand, Region } from '@/types/constants';
import type { ApiClientConfig } from '@/types/config';
import type { Experiment } from '@/types/aggregation/home';
import { getConfig } from '../config/config';
import { buildApiUrl, buildUrl } from './build-url';
import { INCENTIVE_GAMES_RGI } from '../reward-machine/constants';
import { AMPLITUDE_EXPERIMENTS, isExperimentEnabled } from '@/utils/experiments';
import { GAME_PLAY_PATH_PREFIX } from './game-play-path';

export type LauncherUrlProps = {
    providerName?: string;
    region?: Region;
    brand?: Brand;
    gameName?: string;
    rgi?: string;
    ganId?: number | string | null;
    providerRef?: string;
    gameId?: string;
    gameUid?: string;
    theme?: string;
    page?: string;
    idNative?: string | null;
    idNativeXsell?: string | null;
};

export type LauncherUrlResolutionContext = {
    experiments: Experiment;
    isAndroidWrapper: boolean;
    isIosWrapper: boolean;
    isDesktop: boolean;
};

const isRewardMachineGame = (rgi?: string) => {
    return rgi && rgi === INCENTIVE_GAMES_RGI;
};

/** Query-only launcher URLs need `/game-play/?…` so CloudFront `/game-play/*` matches (not `/game-play?…`). */
const hostForLauncherQueryUrl = (host: string): string => {
    if (host.endsWith(`${GAME_PLAY_PATH_PREFIX}/`)) {
        return host;
    }
    if (host.endsWith(GAME_PLAY_PATH_PREFIX)) {
        return `${host}/`;
    }
    return host;
};

const resolveLauncherClient = (explicit?: LauncherUrlResolutionContext): ApiClientConfig => {
    const config = getConfig();
    const base = config.apis.LauncherClient;

    let ctx: LauncherUrlResolutionContext | null = explicit ?? null;
    if (ctx === null && typeof window === 'undefined') {
        // eslint-disable-next-line @typescript-eslint/no-require-imports, @typescript-eslint/no-var-requires -- server-only; avoid top-level import for client bundles
        const { getServerContext } = require('@/store/server-context');
        const s = getServerContext();
        ctx = {
            experiments: s.experiments,
            isAndroidWrapper: s.isAndroidWrapper,
            isIosWrapper: s.isIosWrapper,
            isDesktop: s.isDesktop
        };
    }
    const isMobileWebForSameDomain = ctx !== null && !ctx.isAndroidWrapper && !ctx.isIosWrapper && !ctx.isDesktop;

    const isGameLaunchInLobbyEnabled =
        ctx !== null && isExperimentEnabled(ctx.experiments, AMPLITUDE_EXPERIMENTS.SAME_DOMAIN_LAUNCH);
    const sameDomainFromStaticConfig = Boolean(config.sameOriginGameLauncher);

    if (ctx !== null && isMobileWebForSameDomain && (isGameLaunchInLobbyEnabled || sameDomainFromStaticConfig)) {
        return { ...base, host: config.domain, pathPrefix: GAME_PLAY_PATH_PREFIX };
    }

    return base;
};

export const buildLauncherBaseUrl = (resolution?: LauncherUrlResolutionContext): string => {
    return buildApiUrl(resolveLauncherClient(resolution));
};

export const getLauncherUrl = (
    {
        providerName,
        gameName,
        rgi,
        ganId,
        providerRef,
        region,
        brand,
        gameId,
        gameUid,
        theme,
        page,
        idNative,
        idNativeXsell
    }: LauncherUrlProps,
    resolution?: LauncherUrlResolutionContext
): string => {
    const host = buildLauncherBaseUrl(resolution);

    if (gameUid && !providerName && !rgi) {
        return `${host}/${gameUid}`;
    }

    if (isRewardMachineGame(rgi)) {
        // TODO: unit test pass adjustments, talk with team for adjustments on IG
        return buildUrl(hostForLauncherQueryUrl(host), {
            providerName,
            region,
            brand,
            gameName,
            rgi,
            theme,
            ...(page ? { page } : {})
        });
    }

    return buildUrl(hostForLauncherQueryUrl(host), {
        providerName,
        region,
        brand,
        gameName,
        rgi,
        ganId,
        providerRef,
        gameId,
        gameUid,
        ...(idNative && { idNative: idNative }),
        ...(idNativeXsell && { idNativeXsell: idNativeXsell })
    });
};
